import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { motion } from "framer-motion";

interface TermsOfServiceProps {
  onBack: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <Box sx={{ py: 4, pb: 8, minHeight: "100vh" }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ mb: 3 }}
            variant="outlined"
          >
            Back to Home
          </Button>

          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ mb: 3, fontWeight: 700 }}
            >
              Terms of Service
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              Welcome to Dad2Kid! These Terms of Service govern your use of our
              website and services. By accessing or using Dad2Kid, you agree to
              be bound by these terms.
            </Typography>

            <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
              1. Acceptance of Terms
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              By accessing or using Dad2Kid, you accept and agree to be bound by
              the terms and provision of this agreement. If you do not agree to
              abide by the above, please do not use this service.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              2. Description of Service
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              Dad2Kid is a platform that provides:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Personalized activity recommendations for families"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Step-by-step activity instructions"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Progress tracking and family bonding tools"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Community features and support"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              3. User Accounts
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              To access certain features, you must create an account. You agree
              to:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Provide accurate and complete information"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Maintain the security of your account credentials"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Notify us immediately of any unauthorized use"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Accept responsibility for all activities under your account"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              4. Acceptable Use
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              You agree not to use the service to:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Violate any applicable laws or regulations"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Harass, abuse, or harm others"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Upload or share inappropriate content"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Attempt to gain unauthorized access to our systems"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Interfere with the proper functioning of the service"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              5. Content and Intellectual Property
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              All content on Dad2Kid, including but not limited to text,
              graphics, images, and software, is the property of Dad2Kid or its
              licensors and is protected by copyright laws.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              You retain ownership of content you submit, but grant us a license
              to use, modify, and distribute it for the purpose of providing our
              service.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              6. Privacy and Data Protection
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              Your privacy is important to us. Our collection and use of
              personal information is governed by our Privacy Policy, which is
              incorporated into these Terms of Service by reference.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              7. Disclaimers and Limitations
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE
              DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT
              LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              IN NO EVENT SHALL DAD2KID BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR
              RELATING TO YOUR USE OF THE SERVICE.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              8. Safety and Supervision
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              Activities suggested by Dad2Kid are intended to be performed under
              adult supervision. You are responsible for:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Ensuring activities are appropriate for your children's age and abilities"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Providing adequate supervision during activities"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Following safety guidelines and instructions"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Using common sense and parental judgment"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              9. Termination
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We may terminate or suspend your account and access to the service
              at any time, with or without cause, with or without notice. You
              may terminate your account at any time by contacting us or using
              the account deletion feature.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              10. Changes to Terms
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We reserve the right to modify these terms at any time. We will
              notify users of any material changes via email or through the
              service. Your continued use of the service after changes
              constitutes acceptance of the new terms.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              11. Governing Law
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              These terms shall be governed by and construed in accordance with
              the laws of California, without regard to its conflict of law
              provisions.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              12. Contact Information
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              If you have any questions about these Terms of Service, please
              contact us:
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> legal@dad2kid.com
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Address:</strong> 123 Family Street, San Francisco, CA
                94102
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Phone:</strong> +1 (555) 123-4567
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              By using Dad2Kid, you acknowledge that you have read, understood,
              and agree to be bound by these Terms of Service.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TermsOfService;
